import api from './api';
import { can } from './permissions';
import { certaintyOf, loadAiSettings } from './aiThresholds';

/*
 * Specimen handoffs (routes/specimen_handoffs.py, migration 0097; phase B6):
 * a find handed to the lab, as a pickup (lab staff come for it) or a drop-off
 * (its collector brings it). What the client needs of them in one place: the
 * states and their words, the rare-find rule of the live prompt, the request
 * a form makes and the body the server takes.
 *
 * THE LIVE PROMPT (the user's decision for B6): the moment the AI answers in
 * the field, with the collector still standing by the find. It is offered
 * when the answer is High certainty (certaintyOf: the top candidate, not sent
 * for review) and that candidate is rare -- the server's own rarity scales
 * (GET /rarity?ai_id=, the requester's own answer): rare or very rare in
 * Israel, endemic, or threatened, at species or below. The server's
 * confirmation trigger (routes/rarity_index.py flagged) reads the same scales.
 * Never for a guest of an event, nor anyone without handoff:request and
 * rarity:read.
 */

export const OPEN = Object.freeze(['invited', 'requested', 'assigned', 'scheduled', 'collected']);
export const CLOSED = Object.freeze(['received', 'cancelled', 'declined']);
export const METHODS = Object.freeze(['pickup', 'drop_off']);
export const CONDITIONS = Object.freeze(['fresh', 'drying', 'decaying']);
export const PHONE = /^\+?[0-9 ().-]{6,32}$/;
export const MAX_NOTE = 500;
export const MIN_REASON = 3;
const BELOW_GENUS = new Set(['SPECIES', 'SUBSPECIES', 'VARIETY', 'FORM']);

const nameKey = name => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();

export const isOpen = handoff => !!handoff && OPEN.includes(handoff.status);
export const mayAskForHandoff = user => can(user, 'handoff:request');

/** Why a /rarity candidate is a rare find: [] when it is not. */
export function rareReasonsOf(candidate) {
  const scales = candidate?.scales || {};
  const reasons = [];
  const tier = scales.national?.tier;
  if (tier === 'rare' || tier === 'very_rare') reasons.push(`national_${tier}`);
  if (scales.status?.endemic) reasons.push('endemic');
  if (scales.status?.threatened) reasons.push('threatened');
  return reasons;
}

/**
 * The rare find in an AI answer, or null: {aiId, taxonName, reasons}. Asks
 * the server only when the answer is High certainty and the person may ask
 * for a handoff; never throws (a find the client could not check is simply
 * not prompted for -- the confirmation trigger still catches it later).
 */
export async function rareFindOf(result, user, { client = api, settings = loadAiSettings, config } = {}) {
  try {
    if (!result?.ai_id || !mayAskForHandoff(user) || !can(user, 'rarity:read')) return null;
    const top = result.predictions?.[0];
    const name = top?.taxon_name || top?.taxon;
    if (!name) return null;
    const { thresholds } = (await settings()) || {};
    if (certaintyOf(result, thresholds) !== 'high') return null;
    const res = await client.get('/rarity', { params: { ai_id: result.ai_id }, ...(config || {}) });
    const candidates = res?.data?.candidates || [];
    const match = candidates.find(c => nameKey(c.taxon_name) === nameKey(name));
    if (!match || !BELOW_GENUS.has(String(match.rank || '').toUpperCase())) return null;
    const reasons = rareReasonsOf(match);
    return reasons.length ? { aiId: result.ai_id, taxonName: match.taxon_name, reasons } : null;
  } catch {
    return null;
  }
}

/** A datetime-local value ("2026-10-24T09:30") as an ISO moment with its zone, or null. */
export function momentOf(local) {
  if (!local) return null;
  const when = new Date(local);
  return Number.isNaN(when.getTime()) ? null : when.toISOString();
}

/** An ISO moment as a datetime-local value in this device's zone ('' for none). */
export function localOf(iso) {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}T${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/**
 * A request form's fields checked as the server checks them:
 * {request, errors}. `errors` maps a field to its message key under
 * handoffs:form.errors; `request` is null while there are any.
 */
export function cleanRequest(form) {
  const errors = {};
  const text = v => (typeof v === 'string' ? v.trim() : '');
  const method = form?.method;
  if (!METHODS.includes(method)) errors.method = 'method';
  const condition = form?.condition || null;
  if (condition && !CONDITIONS.includes(condition)) errors.condition = 'condition';
  const phone = text(form?.contact_phone);
  if (phone && !PHONE.test(phone)) errors.contact_phone = 'phone';
  const notes = {};
  for (const key of ['condition_note', 'meeting_note']) {
    notes[key] = text(form?.[key]);
    if (notes[key].length > MAX_NOTE) errors[key] = 'tooLong';
  }
  const from = momentOf(form?.window_from);
  const to = momentOf(form?.window_to);
  if (form?.window_from && !from) errors.window_from = 'when';
  if (form?.window_to && !to) errors.window_to = 'when';
  if (to && !from) errors.window_from = 'windowStart';
  if (from && to && new Date(to) <= new Date(from)) errors.window_to = 'windowOrder';
  const point = form?.meeting_point;
  const hasPoint = point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng));
  if (Object.keys(errors).length) return { request: null, errors };
  return {
    errors,
    request: {
      method,
      condition,
      condition_note: notes.condition_note || null,
      contact_phone: phone || null,
      meeting_note: notes.meeting_note || null,
      meeting_point: hasPoint ? { lat: Number(point.lat), lng: Number(point.lng) } : null,
      window_from: from,
      window_to: to,
    },
  };
}

/** The body of POST /handoffs for a request about `sampleId`: a live one
 *  (the AI's prompt) names the answer that prompted it. */
export function handoffBody(request, sampleId, { aiId = null } = {}) {
  const body = { sample_id: sampleId, method: request.method };
  for (const key of ['condition', 'condition_note', 'contact_phone', 'meeting_note', 'meeting_point',
    'window_from', 'window_to']) {
    if (request[key] != null && request[key] !== '') body[key] = request[key];
  }
  if (aiId) {
    body.trigger = 'live';
    body.ai_id = aiId;
  }
  return body;
}

/** My own handoffs by record: {sample_id: handoff}, the open one first, else the newest. */
export async function myHandoffs({ client = api, config } = {}) {
  const res = await client.get('/handoffs', { params: { mine: 1, limit: 200 }, ...(config || {}) });
  const bySample = {};
  for (const h of res?.data?.items || []) {
    const held = bySample[h.sample_id];
    if (!held || (isOpen(h) && !isOpen(held))
        || (isOpen(h) === isOpen(held) && String(h.requested_at) > String(held.requested_at))) {
      bySample[h.sample_id] = h;
    }
  }
  return bySample;
}

/** A status's colour pair (the app's tokens). */
export function statusTone(status) {
  if (status === 'received') return { bg: 'var(--c-success-soft)', fg: 'var(--c-success-fg)' };
  if (status === 'cancelled' || status === 'declined') return { bg: 'var(--c-surface)', fg: 'var(--c-text-muted-fg)' };
  if (status === 'invited') return { bg: 'var(--c-warning-soft)', fg: 'var(--c-warning-fg)' };
  return { bg: 'var(--c-info-soft)', fg: 'var(--c-info-fg)' };
}
